// @ts-nocheck
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'; // eslint-disable-line
import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';

import templatesConfigSlice from './slices/templatesConfig';
import dataSourcesSlice from './slices/dataSources';
import dataElementsSlice from './slices/dataElements';
import currenciesReducer from './slices/currencies';
import authenticationReducer, { logout } from './slices/authentication';
import applicationReducer from './slices/application';
import maintenanceReducer from './slices/maintenance';
import mlServicesReducer from './slices/ml-services';
import sessionReducer, { clearSessionData } from './slices/session';
import casinoInstanceReducer, {
  brandSwitchMiddleware,
  resetCasinoInstance,
  resetBrandSelection,
  setActiveBrand,
  userBrandsList,
  readPendingBrandSessionId,
  wasBrandSelectedByUser,
} from './slices/casino-instance';
import configuratorConfigReducer, { fetchConfiguratorConfig, resetConfiguratorConfig } from './slices/configurator-config';
import ownerWallet, { resetWallet } from './slices/wallet';
import aiCreditsReducer, { resetAiCredits } from './slices/aiCredits';
import creativeJobsReducer, { resetCreativeJobs } from './slices/creativeJobs';
import ownerNotificationsReducer, { resetOwnerNotifications } from './slices/ownerNotifications';
import campaignStatusReducer from './slices/campaignStatus';
import userPreferencesReducer, {
  resetUserPreferences,
  fetchUserPreferences,
  saveUserPreferences,
  planPrefsMigration,
  selectPreferencesStaleForUser,
  PREFERENCE_KEYS,
} from './slices/userPreferences';
import acquisitionBudgetReducer from './slices/acquisitionBudget';
import userSessionsReducer, { resetUserSessions } from './slices/userSessions';
import ownerHubStatsReducer, { resetOwnerHubStats } from './slices/ownerHubStats';
import casinoDomainInfoReducer from './slices/casinoDomainInfo';
import blogReducer, { resetBlog } from './slices/blog';
import studioPeriodReducer, { resetStudioPeriod } from './slices/studioPeriod';
import ftuxReducer, { resetFtux } from '@/nova/features/ftux/store/ftuxSlice';
import { analyticsConversionsListener } from './analyticsConversionsListener';
import { clearVisitorContext } from './visitor-context-teardown';

import { rootSaga } from './sagas';

// @ts-ignore
const asyncDispatchMiddleware = (store) => (next) => (action) => {
  let syncActivityFinished = false; // @ts-ignore
  let actionQueue = [];

  function flushQueue() {
    // @ts-ignore
    actionQueue.forEach((a) => store.dispatch(a)); // flush queue
    actionQueue = [];
  }
  // @ts-ignore
  function asyncDispatch(asyncAction) {
    // @ts-ignore
    actionQueue = actionQueue.concat([asyncAction]);

    if (syncActivityFinished) {
      flushQueue();
    }
  }

  const actionWithAsyncDispatch = Object.assign({}, action, { asyncDispatch });

  const res = next(actionWithAsyncDispatch);
  syncActivityFinished = true;
  flushQueue();

  return res;
};

const sagaMiddleware = createSagaMiddleware();

// ── Central session teardown ────────────────────────────────────────────────
// The auth slice's `logout` reducer used to clear ONLY the auth tokens, leaving
// the configurator session id, the selected-brand id and the pending-deploy
// session id behind in localStorage — so a stale (or previous user's) session
// bled into the next load (most visibly as a '?' in the brand switcher). This
// listener makes `logout` the single teardown chokepoint that ALSO clears the
// session and brand slices, so no session-scoped state outlives a valid session.
//
// Keyed on `logout` (not `authenticationCheck.rejected`) deliberately: the boot
// check rejects on EVERY unauthenticated visit, and tearing down there would
// wipe the fresh `sessionId` that the register/login funnel's `sessionEnsure()`
// fetches, breaking sign-up. Stale-token detection instead flows through the
// 401 interceptor (`utils/auth-interceptor`), which dispatches `logout()`.
const sessionTeardownListener = createListenerMiddleware();
sessionTeardownListener.startListening({
  actionCreator: logout,
  effect: (action, api) => {
    api.dispatch(clearSessionData());
    api.dispatch(resetCasinoInstance());
    // Drop the saved preferences (profile / billing / comms / lastBrandId) so a
    // shared browser never carries one owner's data into the next session.
    api.dispatch(resetUserPreferences());
    // Same for the cached per-brand Stats figures — without this, the next owner
    // on a shared browser can read the previous owner's numbers on first paint
    // for any brand id they both have access to (the SPA logout does no reload).
    api.dispatch(resetOwnerHubStats());
    // And the owner-scoped wallet (balances, operations, transactions, withdrawal
    // requests, invoices) — all resolved from the token, so the previous owner's
    // money figures must not linger into the next session on a shared browser.
    api.dispatch(resetWallet());
    // And the owner-scoped AI credits balance/ledger — resolved from the token, so the
    // previous owner's credits must not linger into the next session on a shared browser.
    api.dispatch(resetAiCredits());
    // And the owner-scoped creative jobs (the generating tray).
    api.dispatch(resetCreativeJobs());
    // And the owner's notifications (the bell).
    api.dispatch(resetOwnerNotifications());
    // And the owner's active-login list (GET /auth/sessions): identity-scoped and
    // one-shot behind a `!loaded` guard, with revoke actions that target those
    // tokens — without this the next owner's Account section shows, and can revoke,
    // the previous owner's sessions.
    api.dispatch(resetUserSessions());
    // And the picked date range, so the next owner opens every page on its own default.
    api.dispatch(resetStudioPeriod());
    // Drop the VISITOR-scoped context: the session's explicit UI-language pick (so the next
    // owner on a shared browser doesn't inherit, and flush to their prefs, the previous owner's
    // language choice) and the marketing-site `startProperties` hand-off (durable sessionStorage
    // keys: flow, market ids, tracking, package, template, locale-override, raw stash, so they
    // can't inherit the previous owner's campaign attribution / flow / tracking either).
    //
    // Scoped, because ONE caller signs out without the visitor changing: the register / sign-in
    // funnel logs the current identity out so the session it is about to mint can't be stamped
    // with the previous owner's token. Both keys are read moments later (by the mint, and by the
    // magic-link email), so that path passes `keepVisitorContext` and keeps them. See
    // {@link clearVisitorContext}.
    clearVisitorContext(action.payload);
    // Blog articles/categories are brand-scoped content; never carry them across owners.
    api.dispatch(resetBlog());
    // And the owner's FTUX - Getting Started run (mirrored from their preferences).
    api.dispatch(resetFtux());
    // The configurator config is now OWNER-SCOPED (enriched with the owner's token), so drop it too — the
    // next owner on a shared browser would otherwise reuse the previous owner's config (every consumer
    // gates on `!config`, and session.ts mints currency from it).
    api.dispatch(resetConfiguratorConfig());
  },
});

// ── Brand scoping (blog) ────────────────────────────────────────────────────
// The blog is per-brand (the spark-blog tenant is the active brand's id), so a
// brand switch drops the previous brand's articles/categories; the blog pages'
// guard effect sees `loaded: false` and refetches under the new tenant. Also
// fires on boot-time auto-resolution (undefined → id counts as a change), which
// clears a possible "No brand selected" fetch error from a too-early load.
sessionTeardownListener.startListening({
  actionCreator: setActiveBrand,
  effect: (action, api) => {
    const previous = api.getOriginalState().casinoInstance.activeBrand?.id;
    if (previous !== action.payload.id) {
      api.dispatch(resetBlog());
    }
  },
});

// ── Identity-change teardown (impersonation without logout) ──────────────────
// A support agent can sign in AS one owner, then another, from the SAME browser
// via magic links WITHOUT an explicit sign-out between them. That path swaps the
// auth token + user but never dispatches `logout`, so the user-scoped slices kept
// the PREVIOUS owner's data — most visibly the wrong profile / billing / comms in
// Settings, because the prefs GET is one-shot (every fetch site guards on
// `!loaded`, which is reset only by the logout teardown above).
//
// The prefs are tagged with the identity they were fetched for; when the
// authenticated `user_id` no longer matches (`selectPreferencesStaleForUser`), wipe
// the user-scoped slices so the existing fetch guards reload the CURRENT owner's
// data. A token REFRESH keeps the same `user_id`, so this never fires on routine
// rotation. We use `resetBrandSelection` (not `resetCasinoInstance`) so the brand
// LIST the magic-link flow just re-fetched for the new owner survives — only the
// stale persisted brand choice + cached Stats figures are dropped.
const identityChangeListener = createListenerMiddleware();
identityChangeListener.startListening({
  predicate: (_action, currentState) => selectPreferencesStaleForUser(currentState),
  effect: (_action, api) => {
    api.dispatch(resetUserPreferences());
    api.dispatch(resetOwnerHubStats());
    api.dispatch(resetBrandSelection());
    api.dispatch(resetWallet());
    // And the owner-scoped AI credits balance/ledger — resolved from the token, so the
    // previous owner's credits must not linger into the next session on a shared browser.
    api.dispatch(resetAiCredits());
    // And the owner-scoped creative jobs (the generating tray).
    api.dispatch(resetCreativeJobs());
    // And the owner's notifications (the bell).
    api.dispatch(resetOwnerNotifications());
    // The active-login list is identity-scoped too (see the logout teardown), so a
    // magic-link identity swap must drop it or the new owner inherits the previous
    // owner's sessions in Settings -> Account.
    api.dispatch(resetUserSessions());
    // And the picked date range, so the next owner opens every page on its own default.
    api.dispatch(resetStudioPeriod());
    // Blog content is owner/brand-scoped: an impersonation swap must not carry one
    // owner's drafts into the next. (go-studio lacks this path — OH-only teardown.)
    api.dispatch(resetBlog());
    // And the previous owner's FTUX - Getting Started run.
    api.dispatch(resetFtux());
    // The configurator config is owner-scoped (token-enriched) too, so an impersonation swap must drop it
    // or the next owner inherits the previous owner's config (and session.ts mints currency from it).
    api.dispatch(resetConfiguratorConfig());
    // Unlike the other reset-then-refetch slices, the config consumers (useProductScore /
    // useGamificationFeatures / usePendingUpgradeNotices) fetch ONCE behind a one-shot ref, so they will not
    // reload after the reset on this no-navigation swap. Refetch the new owner's config here so those pages
    // don't sit on a stale/empty config (the requestId guard makes the previous owner's in-flight response a
    // no-op if it lands after). Only on this listener: the logout path navigates to /login (no consumer).
    api.dispatch(fetchConfiguratorConfig());
    // And the visitor-scoped context (language pick + marketing hand-off), so an impersonation
    // owner-swap doesn't carry one owner's language choice, campaign attribution, flow or
    // tracking into the next owner. Called with NO options, so this is the full teardown: the
    // scoped variant exists only for the register / sign-in funnel.
    //
    // Load-bearing and invisible: this listener CANNOT fire during that funnel and quietly undo
    // its `keepVisitorContext`, because `selectPreferencesStaleForUser` requires both
    // `userPreferences.loaded` and a non-null `user_id`, and the funnel's `logout` nulls the
    // user_id and resets the prefs before this predicate is ever evaluated. The flip side is the
    // deliberate trade recorded in {@link clearVisitorContext}: on an A -> B swap the funnel's
    // logout also disarms this listener for B's later magic-link redemption, so the two keys it
    // keeps are no longer cleared at that point either.
    clearVisitorContext();
  },
});

// ── Cross-device brand restore ──────────────────────────────────────────────
// The selected brand persists to localStorage (synchronous, same-device fast
// path) AND to the preferences API (cross-device). On load, `resolveInitialBrand`
// activates the localStorage choice immediately so the switcher is never blank;
// this listener then reconciles against the server value once BOTH the brand list
// and the preferences have loaded — whichever lands last triggers the check. If
// the persisted `lastBrandId` is a brand we have and it differs from what's
// active (e.g. the choice was made on another device), we switch to it.
//
// It fires only on the two bootstrap fetches' `fulfilled`, never on a manual
// switch (those dispatch `setActiveBrand`, and the middleware writes the new id
// back to prefs — so `lastBrandId` already equals the active brand and this
// no-ops). A freshly-deployed brand still wins: while a pending deploy session is
// remembered we defer to the switcher's adoption and skip reconciliation.
const brandPrefSyncListener = createListenerMiddleware();
brandPrefSyncListener.startListening({
  predicate: (action) =>
    fetchUserPreferences.fulfilled.match(action) || userBrandsList.fulfilled.match(action),
  effect: (_action, api) => {
    const state = api.getState();
    if (!state.userPreferences.loaded || !state.casinoInstance.loaded) return;
    // Never override an explicit in-session choice: once the user has switched
    // brands, a late-arriving (or stale, in-flight) server value must not yank
    // them elsewhere. The initial cross-device restore runs before any switch.
    if (wasBrandSelectedByUser()) return;
    if (readPendingBrandSessionId()) return; // a just-created brand takes priority

    const brands = state.casinoInstance.brands;
    const lastBrandId = state.userPreferences.preferences[PREFERENCE_KEYS.lastBrandId];
    if (!brands.length || typeof lastBrandId !== 'string') return;

    const match = brands.find((brand) => brand.id === lastBrandId);
    const activeId = state.casinoInstance.activeBrand?.id;
    if (match && match.id !== activeId) {
      api.dispatch(setActiveBrand(match));
    }
  },
});

// ── Preferences schema hygiene ──────────────────────────────────────────────
// After prefs load, run a self-limiting cleanup: null-tombstone any RETIRED
// top-level keys and stamp the schema version (the bag has no hard delete). It
// touches only keys we own/retired — UNKNOWN keys are surfaced (console) but never
// destroyed, so a newer client's keys survive an older one. `patch` is null once
// applied, so this never re-POSTs.
const prefsMigrationListener = createListenerMiddleware();
prefsMigrationListener.startListening({
  actionCreator: fetchUserPreferences.fulfilled,
  effect: (_action, api) => {
    const { patch, unknownKeys } = planPrefsMigration(api.getState().userPreferences.preferences);
    if (unknownKeys.length > 0) {
      console.warn('[userPreferences] unmanaged keys present (retire via DEPRECATED_PREFERENCE_KEYS):', unknownKeys);
    }
    if (patch) api.dispatch(saveUserPreferences(patch));
  },
});

export const store = configureStore({
  reducer: {
    authentication: authenticationReducer,
    application: applicationReducer,
    templatesConfig: templatesConfigSlice,
    dataSources: dataSourcesSlice,
    dataElements: dataElementsSlice,
    currencies: currenciesReducer,
    maintenance: maintenanceReducer,
    mlServices: mlServicesReducer,
    session: sessionReducer,
    casinoInstance: casinoInstanceReducer,
    configuratorConfig: configuratorConfigReducer,
    ownerWallet: ownerWallet,
    aiCredits: aiCreditsReducer,
    creativeJobs: creativeJobsReducer,
    ownerNotifications: ownerNotificationsReducer,
    campaignStatus: campaignStatusReducer,
    userPreferences: userPreferencesReducer,
    acquisitionBudget: acquisitionBudgetReducer,
    userSessions: userSessionsReducer,
    ownerHubStats: ownerHubStatsReducer,
    casinoDomainInfo: casinoDomainInfoReducer,
    blog: blogReducer,
    studioPeriod: studioPeriodReducer,
    ftux: ftuxReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      // brandSwitchMiddleware first so it can defer a brand switch before the
      // reducer/saga see it (drives the cross-page brand fade).
    })
      // Listener prepended so the teardown effect observes logout / auth-check
      // rejection before downstream middleware.
      .prepend(sessionTeardownListener.middleware)
      .prepend(identityChangeListener.middleware)
      .prepend(brandPrefSyncListener.middleware)
      .prepend(prefsMigrationListener.middleware)
      .prepend(analyticsConversionsListener.middleware)
      .concat(brandSwitchMiddleware, asyncDispatchMiddleware, sagaMiddleware),
});

// Expose store globally for popup duplicate prevention logic (debug / controlled usage)
// @ts-ignore
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.store = store;
}

sagaMiddleware.run(rootSaga);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
